import { Link } from 'react-router-dom';
import { Calendar, Gauge, Fuel, Settings, MapPin, Heart, Eye, Phone } from 'lucide-react';
import { formatPrice, formatMileage, formatYear } from '../services/api';

const IMAGE_API_BASE =
  (import.meta.env.VITE_API_URL || 'http://localhost:5000').replace(/\/api\/?$/, '');

const resolveImageUrl = (imageUrl, { allowNull = false } = {}) => {
  if (!imageUrl) {
    return allowNull
      ? null
      : 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="100%" height="100%" fill="%2364748b"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="white" font-size="20">Sem Imagem</text></svg>';
  }
  if (imageUrl.startsWith('http')) return imageUrl;
  // Caminhos vindos do backend (uploads)
  if (imageUrl.startsWith('/uploads')) {
    return `${IMAGE_API_BASE}${imageUrl}`;
  }
  // Nomes de arquivo simples (ex.: vehicle-123.png) - assumir que estão em /uploads/vehicles
  if (!imageUrl.startsWith('/') && !imageUrl.includes('://')) {
    return `${IMAGE_API_BASE}/uploads/vehicles/${imageUrl}`;
  }
  // Outros caminhos (ex: /assets/...) são servidos pelo frontend
  return imageUrl;
};

export const VehicleCard = ({ vehicle }) => {
  const {
    id,
    _id,
    brand,
    model,
    year,
    price,
    mileage,
    fuelType,
    fuel_type,
    transmission,
    color,
    images,
    status
  } = vehicle;

  // Usar id ou _id dependendo da fonte dos dados
  const vehicleId = id || _id;
  const fuel = fuelType || fuel_type;

  const mainImage =
    images && images.length > 0
      ? resolveImageUrl(images[0])
      : resolveImageUrl(null);
  const isAvailable = status === 'Disponível' || status === 'disponivel';

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Disponível':
      case 'disponivel':
        return 'available';
      case 'Vendido':
      case 'vendido':
        return 'sold';
      case 'Reservado':
      case 'reservado':
        return 'reserved';
      default:
        return 'available';
    }
  };

  return (
    <div className="vehicle-card">
      {/* Imagem do veículo */}
      <div className="relative overflow-hidden">
        <img
          src={mainImage}
          alt={`${brand} ${model} ${year}`}
          className="vehicle-image"
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/400x300/64748b/ffffff?text=Sem+Imagem';
          }}
        />
        
        {/* Status badge */}
        <div className="vehicle-badge absolute top-4 right-4">
          <span className={`vehicle-badge ${getStatusBadge(status)}`}>
            {status === 'disponivel' ? 'Disponível' : 
             status === 'vendido' ? 'Vendido' : 
             status === 'reservado' ? 'Reservado' : status}
          </span>
        </div>

        {/* Overlay com ações rápidas */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="flex space-x-2">
            <button className="p-2 bg-white/90 hover:bg-white rounded-full transition-colors">
              <Heart className="h-5 w-5 text-gray-700" />
            </button>
            <Link
              to={`/vehicle/${vehicleId}`}
              className="p-2 bg-white/90 hover:bg-white rounded-full transition-colors"
            >
              <Eye className="h-5 w-5 text-gray-700" />
            </Link>
            <button className="p-2 bg-primary hover:bg-primary-600 rounded-full transition-colors">
              <Phone className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Preço destacado */}
        <div className="absolute bottom-4 left-4">
          <div className="bg-white/95 dark:bg-dark-800/95 backdrop-blur-sm rounded-lg px-3 py-2">
            <p className="text-lg font-bold text-primary">
              {formatPrice(price)}
            </p>
          </div>
        </div>
      </div>

      {/* Conteúdo do card */}
      <div className="p-6">
        {/* Título */}
        <div className="mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
            {brand} {model}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {formatYear(year)} • {formatMileage(mileage)}
          </p>
        </div>

        {/* Especificações */}
        <div className="space-y-3 mb-6">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
              <Fuel className="h-4 w-4 text-primary" />
              <span>{fuel}</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
              <Settings className="h-4 w-4 text-primary" />
              <span>{transmission}</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
            <MapPin className="h-4 w-4 text-primary" />
            <span>Cor: {color}</span>
          </div>
        </div>

        {/* Botões de ação */}
        <div className="flex space-x-3">
          <Link
            to={`/vehicle/${vehicleId}`}
            className="btn btn-outline flex-1 text-center"
          >
            Ver Detalhes
          </Link>
          <button
            className="btn btn-primary flex-1"
            disabled={!isAvailable}
          >
            {isAvailable ? 'Interessado' : 
             status === 'vendido' ? 'Vendido' : 
             status === 'reservado' ? 'Reservado' : 'Indisponível'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente para lista de veículos
export const VehicleGrid = ({ vehicles, loading = false }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {[...Array(8)].map((_, index) => (
          <div key={index} className="vehicle-card animate-pulse">
            <div className="h-48 bg-gray-200 dark:bg-dark-700 rounded-t-xl"></div>
            <div className="p-6 space-y-4">
              <div className="h-4 bg-gray-200 dark:bg-dark-700 rounded w-3/4"></div>
              <div className="h-6 bg-gray-200 dark:bg-dark-700 rounded w-1/2"></div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 dark:bg-dark-700 rounded"></div>
                <div className="h-3 bg-gray-200 dark:bg-dark-700 rounded w-5/6"></div>
              </div>
              <div className="flex space-x-3">
                <div className="h-10 bg-gray-200 dark:bg-dark-700 rounded flex-1"></div>
                <div className="h-10 bg-gray-200 dark:bg-dark-700 rounded flex-1"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!vehicles || vehicles.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-24 h-24 bg-gray-100 dark:bg-dark-700 rounded-full flex items-center justify-center mx-auto mb-6">
          <Calendar className="h-12 w-12 text-gray-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Nenhum veículo encontrado
        </h3>
        <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
          Tente ajustar os filtros de busca ou volte mais tarde para ver novos veículos.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {vehicles.map((vehicle) => (
        <VehicleCard key={vehicle.id || vehicle._id} vehicle={vehicle} />
      ))}
    </div>
  );
};

// Componente para veículos em destaque (layout diferente)
export const FeaturedVehicleCard = ({ vehicle }) => {
  const {
    id,
    _id,
    brand,
    model,
    year,
    price,
    mileage,
    fuelType,
    fuel_type,
    transmission,
    images,
    status
  } = vehicle;

  const vehicleId = id || _id;
  const fuel = fuelType || fuel_type;

  const mainImage =
    images && images.length > 0
      ? resolveImageUrl(images[0])
      : resolveImageUrl(null);
  const isAvailable = status === 'Disponível' || status === 'disponivel';

  return (
    <div className="group relative overflow-hidden rounded-2xl bg-white dark:bg-dark-800 shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-dark-700">
      {/* Imagem */}
      <div className="relative h-64 overflow-hidden">
        <img
          src={mainImage}
          alt={`${brand} ${model} ${year}`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/600x400/64748b/ffffff?text=Sem+Imagem';
          }}
        />
        
        {/* Overlay gradiente */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        
        {/* Badge de destaque */}
        <div className="absolute top-4 left-4">
          <span className="bg-accent text-white px-3 py-1 rounded-full text-sm font-semibold">
            Destaque
          </span>
        </div>
        
        {/* Preço */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="bg-white/95 dark:bg-dark-800/95 backdrop-blur-sm rounded-lg px-4 py-3">
            <p className="text-2xl font-bold text-primary">
              {formatPrice(price)}
            </p>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="p-6">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {brand} {model}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {formatYear(year)} • {formatMileage(mileage)} • {fuel} • {transmission}
        </p>
        
        <div className="flex space-x-3">
          <Link
            to={`/vehicle/${vehicleId}`}
            className="btn btn-primary flex-1"
          >
            Ver Detalhes
          </Link>
          <button
            className="btn btn-outline"
            disabled={!isAvailable}
          >
            {isAvailable ? 'Interessado' : 
             status === 'vendido' ? 'Vendido' : 
             status === 'reservado' ? 'Reservado' : 'Indisponível'}
          </button>
        </div>
      </div>
    </div>
  );
};